import {useCallback, useEffect, useRef} from 'react';
import {useAppDispatch} from 'app/store';
import Toast from 'components/Basic/Toast';
import {ROUTES} from 'constants/routes';
import {messagingActions, selectParticipantId} from 'features/Messaging';
import {CurrentUser, WindowCustomEvent} from 'interfaces';
import {
  Connection,
  IncomingMessage,
  MESSAGE_TYPE,
  MessageReceipt,
  TypingIndicatorTypes,
} from 'interfaces/Pages/Messaging';
import {useSelector} from 'react-redux';
import {MessagingService} from 'services/api';
import {CometChatService} from 'services/api/CometChat';
import {history, isProvider} from 'utils';
import Logger from 'utils/Logger';

import {CometChat} from '@cometchat-pro/chat';

const messageListenerId = 'messageListener' + new Date().getTime();
const userStatusListenerId = 'userStatusListener' + new Date().getTime();

const playNotificationSound = () => {
  try {
    new Audio('/notification.mp3').play();
  } catch (error) {
    Logger.error(error);
  }
};
/**
 * useCometChatListener
 *
 * @param user - logged in user: CurrentUser
 */
export function useCometChatListener(user: CurrentUser) {
  const pathname = window.location.pathname;
  const dispatch = useAppDispatch();
  const participantId = useSelector(selectParticipantId);
  const isUserLoggedInOnCometChat = useRef<boolean>(false);

  const messageRoute = isProvider(user)
    ? ROUTES.PROVIDER.MESSAGE
    : ROUTES.MESSAGE;

  const openMessage = (message: IncomingMessage) => {
    history.push({pathname: messageRoute, state: message});
  };

  const checkChatting = useCallback(
    (senderId: string) => {
      return (
        participantId === senderId &&
        [ROUTES.PROVIDER.MESSAGE, ROUTES.MESSAGE].includes(pathname)
      );
    },
    [participantId, pathname],
  );

  const updateMessage = useCallback(
    (message: IncomingMessage) => {
      const isChatting = checkChatting(message.sender.uid);

      dispatch(messagingActions.addIncomingMessage({message, isChatting}));

      if (isChatting) {
        MessagingService.markAsRead([message]);

        if (typeof window !== 'undefined') {
          const event = new CustomEvent(WindowCustomEvent.NewMessage, {
            detail: message,
          });
          window.dispatchEvent(event);
        }
      }

      MessagingService.markAsDelivered([message]);
    },
    [dispatch, checkChatting],
  );

  const showNotification = useCallback(
    (message: IncomingMessage) => {
      const isChatting = checkChatting(message.sender.uid);

      if (isChatting) {
        return;
      }
      if (message.type === MESSAGE_TYPE.TEXT) {
        Toast({
          type: 'chat_notification',
          title: message.sender.name,
          message: message.data?.text || 'You have a new message',
          toastId: message.id,
          callback: () => openMessage(message),
          autoClose: false,
        });
      } else {
        Toast({
          type: 'chat_notification',
          title: message.sender.name,
          message: `${message.sender.name} sent an attachment`,
          toastId: message.id,
          callback: () => openMessage(message),
        });
      }
      playNotificationSound();
    },
    [checkChatting],
  );

  const initListeners = useCallback(() => {
    CometChat.addMessageListener(
      messageListenerId,
      new CometChat.MessageListener({
        onTextMessageReceived: (textMessage: CometChat.TextMessage) => {
          const message = textMessage as unknown as IncomingMessage;
          if (message.receiver.uid !== user?._id) {
            return;
          }

          showNotification(message);
          updateMessage(message);
        },
        onMediaMessageReceived: (mediaMessage: CometChat.TextMessage) => {
          const message = mediaMessage as unknown as IncomingMessage;

          if (message.receiver.uid !== user?._id) {
            return;
          }

          showNotification(message);
          updateMessage(message);
        },
        onCustomMessageReceived: (customMessage: CometChat.CustomMessage) => {
          Logger.info('Custom message received successfully', customMessage);
        },
        onMessagesDelivered: (messageReceipt: CometChat.MessageReceipt) => {
          const receipt = messageReceipt as unknown as MessageReceipt;
          if (receipt.messageId && receipt.deliveredAt) {
            dispatch(
              messagingActions.markSentMessageAsDelivered({
                messageId: receipt.messageId,
                deliveredAt: receipt.deliveredAt,
              }),
            );
          }
        },
        onMessagesRead: (messageReceipt: CometChat.MessageReceipt) => {
          const receipt = messageReceipt as unknown as MessageReceipt;
          if (receipt.messageId && receipt.readAt) {
            dispatch(
              messagingActions.markSentMessageAsRead({
                messageId: receipt.messageId,
                readAt: receipt?.readAt,
              }),
            );
          }
        },
        onTypingStarted: (typingIndicator: CometChat.TypingIndicator) => {
          const typingData = typingIndicator as unknown as TypingIndicatorTypes;

          if (typingData) {
            dispatch(
              messagingActions.setUserTypingStatus({
                uid: typingData.sender.uid,
                typing: true,
              }),
            );
          }
        },
        onTypingEnded: (typingIndicator: CometChat.TypingIndicator) => {
          const typingData = typingIndicator as unknown as TypingIndicatorTypes;

          if (typingData) {
            dispatch(
              messagingActions.setUserTypingStatus({
                uid: typingData.sender.uid,
                typing: false,
              }),
            );
          }
        },
      }),
    );

    /** update user presence status */
    CometChat.addUserListener(
      userStatusListenerId,
      new CometChat.UserListener({
        onUserOnline: (onlineUser: CometChat.User) => {
          const userOnlineData = onlineUser as unknown as Connection;

          if (userOnlineData) {
            dispatch(
              messagingActions.setUserPresenceStatus({
                uid: userOnlineData.uid,
                status: userOnlineData.status,
                lastActiveAt: userOnlineData.lastActiveAt,
              }),
            );
          }
        },
        onUserOffline: (offlineUser: CometChat.User) => {
          const userOfflineData = offlineUser as unknown as Connection;
          if (userOfflineData) {
            dispatch(
              messagingActions.setUserPresenceStatus({
                uid: userOfflineData.uid,
                status: userOfflineData.status,
                lastActiveAt: userOfflineData.lastActiveAt,
              }),
            );
          }
        },
      }),
    );
  }, [user?._id, dispatch, showNotification, updateMessage]);

  const loginCometChat = useCallback(async () => {
    try {
      const cometUser = await CometChatService.getLoggedInUser();
      if (!cometUser && user?.cometChatData?.authToken) {
        dispatch(
          messagingActions.loginUserOnCometChat({
            authToken: user.cometChatData.authToken,
            cb: () => {
              isUserLoggedInOnCometChat.current = true;
            },
          }),
        );
      } else if (cometUser?.getUid() === user?.cometChatData?.uid) {
        dispatch({
          type: 'loginUserOnCometChat/fulfilled',
          payload: cometUser,
        });
        isUserLoggedInOnCometChat.current = true;
      } else if (cometUser?.getUid() !== user?.cometChatData?.uid) {
        CometChat.logout().then(() => {
          if (user?.cometChatData?.authToken) {
            dispatch(
              messagingActions.loginUserOnCometChat({
                authToken: user.cometChatData.authToken,
                cb: () => {
                  isUserLoggedInOnCometChat.current = true;
                },
              }),
            );
          }
        });
      }
    } catch (error) {
      isUserLoggedInOnCometChat.current = false;
      Logger.error('comet chat login failed: ', error);
    }
  }, [user?.cometChatData, dispatch]);

  useEffect(() => {
    loginCometChat();

    return () => {
      isUserLoggedInOnCometChat.current = false;
    };
  }, [loginCometChat]);

  useEffect(() => {
    initListeners();

    return () => {
      CometChat.removeMessageListener(messageListenerId);
      CometChat.removeMessageListener(userStatusListenerId);
    };
  }, [initListeners]);

  useEffect(() => {
    /**
     * logout comet chat if user is logged out
     */
    if (!user?._id && isUserLoggedInOnCometChat.current) {
      CometChatService.logout();
    }
  }, [user?._id]);
}
